import { useState } from "react";

// API Logic
const submitContactForm = async (formData) => {
  try {
    const modifiedFormData = {
      ...formData,
      // Prefix subject with hidden site info
      subject: `From AI Assistant - ${formData.subject}`
        ? `From AI Domain: ${formData.subject}`
        : 'From AI Domain',
      site_from: 'https://ai.sitesmanager.uk', // always include this hidden value
    };

    const response = await fetch('https://www.sitesmanager.uk/api/send-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(modifiedFormData),
    });

    const data = await response.json();

    if (!response.ok) throw new Error(data.error || 'Failed to submit form');

    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Form Hook
const useContactForm = (initialState) => {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Email is invalid';
    if (!form.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const result = await submitContactForm(form);
    setLoading(false);

    if (result.success) {
      alert('Thank you for your message! We will get back to you soon.');
      setForm(initialState);
    } else {
      alert(`Error: ${result.error}`);
    }
  };

  return { form, loading, errors, handleChange, handleSubmit, setForm };
};

// Contact Form Component
const ContactForm = () => {
  const initialFormState = {
    name: '',
    email: '',
    phone: '',
    site: '',        // customer's site (visible input)
    subject: '',
    message: ''
  };

  const { form, loading, errors, handleChange, handleSubmit } = useContactForm(initialFormState);

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div className="card shadow">
            <div className="card-body p-4 p-md-5">
              <form onSubmit={handleSubmit}>
                {/* Name */}
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    disabled={loading}
                  />
                  {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>

                {/* Email */}
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    disabled={loading}
                  />
                  {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                </div>

                {/* Phone */}
                <div className="mb-3">
                  <label htmlFor="phone" className="form-label">Phone</label>
                  <input
                    type="tel"
                    className="form-control"
                    id="phone"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                    disabled={loading}
                  />
                </div>

                {/* Customer Site */}
                <div className="mb-3">
                  <label htmlFor="site" className="form-label">Website</label>
                  <input
                    type="url"
                    className="form-control"
                    id="site"
                    name="site"
                    value={form.site}
                    onChange={handleChange}
                    placeholder="Enter your website URL"
                    disabled={loading}
                  />
                </div>

                {/* Hidden site_from */}
                <input type="hidden" name="site_from" value="https://ai.sitesmanager.uk" />

                {/* Subject */}
                <div className="mb-3">
                  <label htmlFor="subject" className="form-label">Subject</label>
                  <input
                    type="text"
                    className="form-control"
                    id="subject"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    placeholder="What is this regarding?"
                    disabled={loading}
                  />
                </div>

                {/* Message */}
                <div className="mb-4">
                  <label htmlFor="message" className="form-label">Message *</label>
                  <textarea
                    className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                    id="message"
                    name="message"
                    rows="4"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Please enter your message here..."
                    disabled={loading}
                  ></textarea>
                  {errors.message && <div className="invalid-feedback">{errors.message}</div>}
                </div>

                {/* Submit */}
                <div className="d-grid">
                  <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending...
                      </>
                    ) : 'Send Message'}
                  </button>
                </div>
              </form>

              {loading && <div className="alert alert-info mt-3 mb-0">Sending your message, please wait...</div>}
            </div>
          </div>
          <div className="mt-4 text-center text-muted">
            <small>We typically respond within 24 hours</small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
